import { useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import SelectComponent from "../../components/SelectComponent";
import { useParams } from "react-router-dom";
import TableComponent from "../../components/TableComponent";
import BackComponent from "../../components/BackComponent";
import ExcelButtonComponent from "../../components/ExcelButtonComponent";
import { createColumn } from "../../utils/tableUtils";
import { tableDateFormatter } from "../../utils/formatDate";
import {
	HeaderComponent,
	Container,
	ContainerHeader,
	FiltersContainer,
	ActionButtonOverlay
} from "../../components/styled/common";
import { useEventContext } from "../../context/EventContext/EventContext";
import { useEventApplicationContext } from "../../context/EventApplicationContext/EventApplicationContext";

const EventParticipantList: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const {
		eventDetail,
		getEventDetails,
		clearEventFormData,
	} = useEventContext();

	const {
		participantList,
		clearParticipantList,
		getEventParticipantList,
		exportEventParticipantExcel,
		operationStatus
	} = useEventApplicationContext(); 

	const [selectedCompanyCode, setSelectedCompanyCode] = useState<string | null>(null);

	const companyOptions = useMemo(() => {
		const map = new Map<string, string>();
		participantList.forEach((p) => {
			if (p.companyCode && !map.has(p.companyCode)) {
				map.set(p.companyCode, p.companyName ? `${p.companyCode} - ${p.companyName}` : p.companyCode);
			}
		});
		return Array.from(map, ([value, label]) => ({ value, label }));
	}, [participantList]);

	const tableData = useMemo(() => {
		return participantList.filter((p) => {
			const companyMatch = !selectedCompanyCode || p.companyCode === selectedCompanyCode;
			return companyMatch;
		});
	}, [participantList, selectedCompanyCode]);

	const isFetching = operationStatus.type === "loading" && operationStatus.operation === "fetch";
	const isExporting = operationStatus.type === "loading" && operationStatus.operation === "export";

	const handleExport = () => {
		if (!id) {
			return;
		}
		exportEventParticipantExcel(id, { companyCode: selectedCompanyCode });
	};

	useEffect(() => {
		if (id) {
			clearParticipantList();
			getEventParticipantList(id);
			getEventDetails(id);
		}

		return () => {
			clearParticipantList();
			clearEventFormData();
		};
	}, [id]);

	const eventHeader = useMemo(() => {
		const localizables = eventDetail?.eventPreview?.eventPreviewLocalizable ?? [];
		return (localizables.find(x => x.culture === "tr-TR") ?? localizables[0])?.header ?? "";
	}, [eventDetail]);

	const columns = useMemo(() => [
		createColumn({
			title: "Ad Soyad",
			dataIndex: "name",
			width: 50,
			sorterType: "string",
			render: (text, record) => `${record.name} ${record.surname}`,
		}),
		createColumn({
			title: "E-posta",
			dataIndex: "email",
			width: 60,
			sorterType: "string",
		}),
		createColumn({
			title: "Telefon",
			dataIndex: "phoneNumber",
			width: 40,
			sorterType: "string",
			render: (text, record) => record.phoneNumber || "-",
		}),
		createColumn({
			title: "Şirket Kodu",
			dataIndex: "companyCode",
			width: 20,
			sorterType: "string",
			render: (text, record) => record.companyCode || "-",
		}),
		createColumn({
			title: "Şirket Adı",
			dataIndex: "companyName",
			width: 50,
			sorterType: "string",
			render: (text, record) => record.companyName || "-",
		}),
		createColumn({
			title: "Başvuru Tarihi",
			dataIndex: "joinDate",
			width: 40,
			sorterType: "string",
			render: (text, record) => (record.joinDate ? tableDateFormatter(record.joinDate, "DD.MM.YYYY HH:mm") : "-"),
		})
	], []);

	return (
		<div>
			<BackComponent text="Başvuru Özetlerine Dön" to="/event-applications" style={{ marginBottom: 15 }} />
			<HeaderComponent>{eventHeader ? `${eventHeader} Katılımcıları` : ""}</HeaderComponent>
			<Container>
				<Spin spinning={isFetching}>
					<ContainerHeader style={{ justifyContent: "space-between" }}>
						<FiltersContainer>
							<SelectComponent
								allowClear
								placeholder="Şirket Seç"
								style={{ width: "12vw" }}
								options={companyOptions}
								value={selectedCompanyCode}
								onChange={(value) => setSelectedCompanyCode(value != null ? String(value) : null)}
							/>
						</FiltersContainer>
						<ActionButtonOverlay>
							<ExcelButtonComponent
								loading={isExporting}
								disabled={!id || tableData.length === 0}
								onClick={handleExport}
							/>
						</ActionButtonOverlay>
					</ContainerHeader>
					<TableComponent
						columns={columns}
						dataSource={tableData as any[]}
					/>
				</Spin>
			</Container>
		</div>
	);
};

export default EventParticipantList;
